import React from "react"
import { useTranslation } from "react-i18next"
import { View } from "react-native"

import styled from "styled-components/native"

import icons from "@treefort/tokens/app/icons"

import { getTextStyleString, TextStyle } from "../lib/text-style"
import Column from "./column"
import Icon from "./icon"
import { SIGN_UP_CARD_MAX_WIDTH_PX } from "./sign-up-option-card"
import Text from "./text"
import TextInput from "./text-input"
import { useTokens } from "./tokens-provider"

export type CheckoutCodeInputState =
  | { type: "idle" }
  | { type: "loading" }
  | { type: "success"; message?: string }
  | { type: "error"; message: string }

const Caption = styled(Text)`
  position: absolute;
  top: 100%;
  left: 0;
  margin-top: ${(props) => props.theme.spacing.xtiny}px;
`

const StyledTextInput = styled(TextInput)<{
  $textStyle: TextStyle
  $type: CheckoutCodeInputState["type"]
}>`
  height: ${({ theme }) => theme.promoCodeInput.height}px;
  border-radius: ${({ theme }) => theme.borderRadius.roundedMedium}px;
  border: ${({ theme }) => theme.spacing.xtiny}px solid
    ${(props) =>
      props.theme.promoCodeInput.borderColor[
        props.$type === "success" ? "success" : "default"
      ]};
  background-color: ${(props) =>
    props.theme.promoCodeInput.backgroundColor[
      props.$type === "success" ? "success" : "default"
    ]};
  padding-left: ${({ theme }) => theme.promoCodeInput.paddingLeft}px;
  padding-right: ${({ theme }) => theme.promoCodeInput.paddingRight}px;
  ${(props) => getTextStyleString(props.$textStyle, props.theme)};
  color: ${(props) =>
    props.$type === "success"
      ? props.theme.colors.text.success
      : props.theme.promoCodeInput.textColor.default};
`

const TagIconContainer = styled.View`
  position: absolute;
  top: 0;
  left: 0;
  bottom: 0;
  justify-content: center;
  padding-left: ${({ theme }) =>
    theme.promoCodeInput.tagIconContainer.paddingLeft}px;
  pointer-events: none;
  z-index: 1;
`

export function CheckoutCodeInput({
  state,
  value,
  autoFocus,
  onChange,
}: {
  state: CheckoutCodeInputState
  value: string
  autoFocus?: boolean
  onChange: (value: string) => void
}) {
  const { tokens } = useTokens()
  const { t } = useTranslation()

  return (
    <Column
      maxWidth={SIGN_UP_CARD_MAX_WIDTH_PX}
      width="100%"
      gap="xtiny"
      alignItems="stretch"
    >
      <View>
        <TagIconContainer>
          <Icon
            source={icons.priceTag}
            size="mediumsmall"
            color={
              state.type === "success"
                ? tokens.colors.success
                : tokens.promoCodeInput.placeholderTextColor
            }
          />
        </TagIconContainer>
        <StyledTextInput
          value={value}
          onChangeText={onChange}
          $type={state.type}
          $textStyle={value.length ? "captionStrong" : "caption"}
          placeholderTextColor={tokens.promoCodeInput.placeholderTextColor}
          placeholder={t("If you have a code, enter it here...")}
          autoFocus={autoFocus}
        />
      </View>
      {state.type === "success" ? (
        <Caption color="success" textStyle="caption">
          {state.message}
        </Caption>
      ) : state.type === "error" ? (
        <Caption color="danger" textStyle="caption">
          {state.message}
        </Caption>
      ) : state.type === "loading" ? (
        <Caption color="secondary" textStyle="caption">
          {t("Verifying...")}
        </Caption>
      ) : null}
    </Column>
  )
}
